// Basic
import { useState } from 'react';
import PropTypes from 'prop-types';
// Store & actions
import useRootOrganizationStore from '@/app/store/rootOrganization';
import useSubscriptionStore from '@/app/store/modules/subscription';
import actionsPosTerminals from '../../../api/actionsPosTerminals';
// Components
import HeaderTitle from '@/common-utils/components/renders/modal/components/headers/HeaderTitle/HeaderTitle';
import FooterStep from '@/common-utils/components/renders/modal/components/footers/FooterStep/FooterStep';
import SubscriptionsTable from '@/app/modules/subscriptions/components/tables/SubscriptionsTable';
import PlansTable from '@/app/modules/plans/components/tables/PlansTable';
import Entity from '@/common-utils/components/renders/entity/Entity';
import { Modal, ModalBody, ModalContent } from '@/common-utils/components/ui/modal';
// Common
import getInitialValueForRowSelected from '@/common-utils/components/renders/dataTable/utils/GetInitialValueForRowSelected';
import subscriptionFields from '@/app/modules/subscriptions/fields/subscriptionFields';
import { useToast } from '@/common-utils/hooks/use-toast';
import useStepConfig from '@/common-utils/hooks/useStepConfig';
import setFiltersEventName from '@/common-utils/components/renders/dataTable/utils/setFiltersEventName';
// Lang
import { nsTerminal } from '@/app/i18n/namespaces';
/**
 * @param root0
 * @param root0.isOpen
 * @param root0.setIsOpen
 * @returns {JSX} ModalMultipleActivationPos
 */
function ModalMultipleActivationPos({ isOpen, setIsOpen }) {
    // Store
    const rootOrganization = useRootOrganizationStore(s => s.rootOrganization);
    const storeRefetchSubscriptions = useSubscriptionStore(s => s.storeRefetchSubscriptions);

    // Constants
    const STEP_TITLE_PREFIX = 'modal.multiple_activation.steps';
    const MAX_STEPS_NUMBER = 3;
    const MAX_PICKS = 50;
    const customQuery = {
        filters: [
            {
                key: subscriptionFields.DIRECT_PROVIDER.key,
                value: rootOrganization.uuid,
                hidden: true
            },
            {
                key: subscriptionFields.STATUS.key,
                value: 'ACTIVATABLE',
                hidden: true
            }
        ]
    };

    // Toast
    const { toast } = useToast();

    // State
    const [step, setStep] = useState(0);
    const [selectedSubscriptions, setSelectedSubscriptions] = useState(getInitialValueForRowSelected());
    const [selectedPlan, setSelectedPlan] = useState(getInitialValueForRowSelected());

    // Api calls
    const onSuccess = () => {
        setIsOpen(false);
        storeRefetchSubscriptions(true);
        toast({
            description: <Entity entity={'modal.multiple_activation.success'} namespace={nsTerminal} />,
            variant: 'success'
        });
    };
    const onError = () => {
        setIsOpen(false);
        toast({
            description: <Entity entity={'modal.multiple_activation.error'} namespace={nsTerminal} />,
            variant: 'danger'
        });
    };
    const multiplePosActivation = actionsPosTerminals.multiplePosActivation(onSuccess, onError);

    // Hooks
    const { stepTitle, disabledNext } = useStepConfig(
        step,
        () => ({
            0: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_pos_terminals`,
                disabledNext: !Object.keys(selectedSubscriptions.rowIds).length
            },
            1: {
                stepTitle: `${STEP_TITLE_PREFIX}.select_plan`,
                disabledNext: !Object.keys(selectedPlan.rowIds).length
            },
            2: {
                stepTitle: `${STEP_TITLE_PREFIX}.confirmation`,
                disabledNext: false
            }
        }),
        [selectedSubscriptions, selectedPlan]
    );

    // Render
    return (
        <Modal open={isOpen} onOpenChange={setIsOpen}>
            <ModalContent className={step === 2 ? 'max-w-3xl' : 'max-w-6xl'}>
                <HeaderTitle title='modal.multiple_activation.title' namespace={nsTerminal} />
                <ModalBody className='modal-max-height overflow-auto'>
                    <Entity
                        entity={stepTitle}
                        data={{ step: step + 1, maxStep: MAX_STEPS_NUMBER }}
                        namespace={nsTerminal}
                        className={'mb-2 flex'}
                    />
                    {step === 0 &&
                        <SubscriptionsTable
                            filtersEventName={setFiltersEventName('subscriptions', true)}
                            customQuery={customQuery}
                            rowsSelected={selectedSubscriptions}
                            setRowsSelected={setSelectedSubscriptions}
                            maxPicks={MAX_PICKS}
                        />
                    }
                    {step === 1 &&
                        <PlansTable
                            filtersEventName={setFiltersEventName('plans', true)}
                            singleSelection={true}
                            rowsSelected={selectedPlan}
                            setRowsSelected={setSelectedPlan}
                        />
                    }
                    {step === 2 &&
                        <>
                            <div className='text-center'>
                                <Entity
                                    entity='modal.multiple_activation.confirmation.text'
                                    namespace={nsTerminal}
                                    data={{ number: selectedSubscriptions.rowsData.length }}
                                />
                            </div>
                            <div className='text-center mt-4'>
                                <Entity entity='modal.multiple_activation.confirmation.question' namespace={nsTerminal} />
                            </div>
                        </>
                    }
                </ModalBody>
                <FooterStep
                    step={step}
                    setStep={setStep}
                    stepsNumber={MAX_STEPS_NUMBER}
                    onValidate={() => {
                        multiplePosActivation.mutate({
                            subscriptionsIds: Object.keys(selectedSubscriptions.rowIds),
                            planId: Object.keys(selectedPlan.rowIds)[0]
                        });
                    }
                    }
                    isLoading={multiplePosActivation.isLoading}
                    disableValidation={disabledNext}
                />
            </ModalContent>
        </Modal>
    );
}

ModalMultipleActivationPos.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    setIsOpen: PropTypes.func.isRequired
};

export default ModalMultipleActivationPos;